/**
 * Staff Auth Service
 * ==================
 * JWT-authenticated API layer for the website control panel.
 * Staff are school employees with is_staff=True.
 *
 * Storage: tokens in localStorage under 'staff_*' keys.
 * 401 responses clear the session and redirect to /website-cp/login.
 */

const BASE_URL    = process.env.VUE_APP_API_BASE || 'https://0x01source.com:9002/api';
const TOKEN_KEY   = 'staff_access';
const REFRESH_KEY = 'staff_refresh';
const PROFILE_KEY = 'staff_profile';

// ── Token storage ──────────────────────────────────────────────────────────

export function getAccessToken()  { return localStorage.getItem(TOKEN_KEY); }
export function getRefreshToken() { return localStorage.getItem(REFRESH_KEY); }
export function isLoggedIn()      { return !!getAccessToken(); }

export function getStoredProfile() {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY) || 'null'); }
  catch { return null; }
}

function saveSession({ tokens, staff }) {
  localStorage.setItem(TOKEN_KEY,   tokens.access);
  localStorage.setItem(REFRESH_KEY, tokens.refresh);
  localStorage.setItem(PROFILE_KEY, JSON.stringify(staff));
}

export function clearSession() {
  [TOKEN_KEY, REFRESH_KEY, PROFILE_KEY].forEach(k => localStorage.removeItem(k));
}

// ── HTTP helpers ───────────────────────────────────────────────────────────

async function request(endpoint, options = {}) {
  const token = getAccessToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(`${BASE_URL}${endpoint}`, { ...options, headers });

  if (res.status === 401) {
    clearSession();
    window.location.href = '/website-cp/login';
    throw new Error('Session expired. Please log in again.');
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const msg  = data.error || data.detail || data.non_field_errors?.[0] || `Error ${res.status}`;
    throw new Error(msg);
  }

  if (res.status === 204) return null;
  return res.json();
}

function buildQs(params = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  return q.toString() ? `?${q.toString()}` : '';
}

const get    = (ep, params)  => request(`${ep}${buildQs(params)}`);
const post   = (ep, data)    => request(ep, { method: 'POST',   body: JSON.stringify(data) });
const patch  = (ep, data)    => request(ep, { method: 'PATCH',  body: JSON.stringify(data) });
const put    = (ep, data)    => request(ep, { method: 'PUT',    body: JSON.stringify(data) });
const del    = (ep)          => request(ep, { method: 'DELETE' });

// ── Auth ───────────────────────────────────────────────────────────────────

async function login(email, password) {
  const data = await post('/staff/auth/login/', { email, password });
  saveSession(data);
  return data;
}

async function logout() {
  const refresh = getRefreshToken();
  try {
    if (refresh) await post('/staff/auth/logout/', { refresh });
  } finally {
    clearSession();
  }
}

// ── Self-service ───────────────────────────────────────────────────────────

const getMe        = ()     => get('/staff/me/');
const updateMe     = (data) => patch('/staff/me/', data);
const getDashboard = ()     => get('/staff/dashboard/');

// ── CMS — Site Settings ────────────────────────────────────────────────────

const getSettings    = ()     => get('/settings/');
const updateSettings = (data) => patch('/settings/', data);

// ── CMS — Pages ───────────────────────────────────────────────────────────

const getPages          = (p)           => get('/pages/', p);
const getPage           = (slug)        => get(`/pages/${slug}/`);
const createPage        = (data)        => post('/pages/', data);
const updatePage        = (slug, data)  => patch(`/pages/${slug}/`, data);
const deletePage        = (slug)        => del(`/pages/${slug}/`);

// ── CMS — Page Sections (nested under page slug) ──────────────────────────

const getPageSections        = (slug)         => get(`/pages/${slug}/sections/`);
const createPageSection      = (slug, data)   => post(`/pages/${slug}/sections/`, data);
const updatePageSection      = (slug, id, d)  => patch(`/pages/${slug}/sections/${id}/`, d);
const deletePageSection      = (slug, id)     => del(`/pages/${slug}/sections/${id}/`);

// ── CMS — Hero Slides ─────────────────────────────────────────────────────

const getHeroSliders    = (p)           => get('/hero-sliders/', p);
const getHeroSlider     = (id)          => get(`/hero-sliders/${id}/`);
const createHeroSlider  = (data)        => post('/hero-sliders/', data);
const updateHeroSlider  = (id, data)    => patch(`/hero-sliders/${id}/`, data);
const deleteHeroSlider  = (id)          => del(`/hero-sliders/${id}/`);

// ── CMS — Principal Message ───────────────────────────────────────────────

const getPrincipalMessages   = (p)        => get('/principal/', p);
const getPrincipalMessage    = (id)       => get(`/principal/${id}/`);
const createPrincipalMessage = (data)     => post('/principal/', data);
const updatePrincipalMessage = (id, d)    => patch(`/principal/${id}/`, d);
const deletePrincipalMessage = (id)       => del(`/principal/${id}/`);

// ── CMS — Program Cards ───────────────────────────────────────────────────

const getProgramCards    = (p)        => get('/programs/', p);
const getProgramCard     = (id)       => get(`/programs/${id}/`);
const createProgramCard  = (data)     => post('/programs/', data);
const updateProgramCard  = (id, d)    => patch(`/programs/${id}/`, d);
const deleteProgramCard  = (id)       => del(`/programs/${id}/`);

const getProgramFeatures   = (programId)  => get(`/programs/${programId}/features/`);
const createProgramFeature = (programId, d) => post(`/programs/${programId}/features/`, d);
const deleteProgramFeature = (id)         => del(`/program-features/${id}/`);

// ── CMS — Partner Logos ───────────────────────────────────────────────────

const getPartners    = (p)        => get('/partners/', p);
const getPartner     = (id)       => get(`/partners/${id}/`);
const createPartner  = (data)     => post('/partners/', data);
const updatePartner  = (id, d)    => patch(`/partners/${id}/`, d);
const deletePartner  = (id)       => del(`/partners/${id}/`);

// ── CMS — News & Events ───────────────────────────────────────────────────

const getNews       = (p)         => get('/news/', p);
const getNewsItem   = (slug)      => get(`/news/${slug}/`);
const createNews    = (data)      => post('/news/', data);
const updateNews    = (slug, d)   => patch(`/news/${slug}/`, d);
const deleteNews    = (slug)      => del(`/news/${slug}/`);

// ── CMS — School Staff Members ────────────────────────────────────────────

const getStaffMembers   = (p)        => get('/staff/', p);
const getStaffMember    = (id)       => get(`/staff/${id}/`);
const createStaffMember = (data)     => post('/staff/', data);
const updateStaffMember = (id, d)    => patch(`/staff/${id}/`, d);
const deleteStaffMember = (id)       => del(`/staff/${id}/`);

// ── CMS — Testimonials ────────────────────────────────────────────────────

const getTestimonials   = (p)        => get('/testimonials/', p);
const getTestimonial    = (id)       => get(`/testimonials/${id}/`);
const createTestimonial = (data)     => post('/testimonials/', data);
const updateTestimonial = (id, d)    => patch(`/testimonials/${id}/`, d);
const deleteTestimonial = (id)       => del(`/testimonials/${id}/`);

// ── CMS — FAQs ────────────────────────────────────────────────────────────

const getFAQs      = (p)        => get('/faqs/', p);
const getFAQ       = (id)       => get(`/faqs/${id}/`);
const createFAQ    = (data)     => post('/faqs/', data);
const updateFAQ    = (id, d)    => patch(`/faqs/${id}/`, d);
const deleteFAQ    = (id)       => del(`/faqs/${id}/`);

// ── CMS — Menu Categories & Items ─────────────────────────────────────────

const getMenuCategories    = (p)        => get('/menu-categories/', p);
const getMenuCategory      = (id)       => get(`/menu-categories/${id}/`);
const createMenuCategory   = (data)     => post('/menu-categories/', data);
const updateMenuCategory   = (id, d)    => patch(`/menu-categories/${id}/`, d);
const deleteMenuCategory   = (id)       => del(`/menu-categories/${id}/`);

const getMenuItems    = (p)        => get('/menu-items/', p);
const getMenuItem     = (id)       => get(`/menu-items/${id}/`);
const createMenuItem  = (data)     => post('/menu-items/', data);
const updateMenuItem  = (id, d)    => patch(`/menu-items/${id}/`, d);
const deleteMenuItem  = (id)       => del(`/menu-items/${id}/`);

// ── CMS — Gallery ─────────────────────────────────────────────────────────

const getGallery        = (p)        => get('/gallery/', p);
const getAlbum          = (id)       => get(`/gallery/${id}/`);
const createAlbum       = (data)     => post('/gallery/', data);
const updateAlbum       = (id, d)    => patch(`/gallery/${id}/`, d);
const deleteAlbum       = (id)       => del(`/gallery/${id}/`);

const getAlbumPhotos    = (albumId)  => get(`/gallery/${albumId}/photos/`);
const addAlbumPhoto     = (albumId, d) => post(`/gallery/${albumId}/photos/`, d);
const deleteAlbumPhoto  = (id)       => del(`/gallery-photos/${id}/`);

// ── CMS — Contacts ────────────────────────────────────────────────────────

const getSubmissions      = (p)        => get('/contact-submissions/', p);
const getSubmission       = (id)       => get(`/contact-submissions/${id}/`);
const updateSubmission    = (id, d)    => patch(`/contact-submissions/${id}/`, d);
const deleteSubmission    = (id)       => del(`/contact-submissions/${id}/`);

const getContactPeople    = (p)        => get('/contact-people/', p);
const getContactPerson    = (id)       => get(`/contact-people/${id}/`);
const createContactPerson = (data)     => post('/contact-people/', data);
const updateContactPerson = (id, d)    => patch(`/contact-people/${id}/`, d);
const deleteContactPerson = (id)       => del(`/contact-people/${id}/`);

// ── CMS — Policies ────────────────────────────────────────────────────────

const getPolicies    = (p)        => get('/policies/', p);
const getPolicy      = (id)       => get(`/policies/${id}/`);
const createPolicy   = (data)     => post('/policies/', data);
const updatePolicy   = (id, d)    => patch(`/policies/${id}/`, d);
const deletePolicy   = (id)       => del(`/policies/${id}/`);

const getPolicyPages    = (policyId)    => get(`/policies/${policyId}/pages/`);
const addPolicyPage     = (policyId, d) => post(`/policies/${policyId}/pages/`, d);
const deletePolicyPage  = (id)          => del(`/policy-pages/${id}/`);

// ── CMS — Admission ───────────────────────────────────────────────────────

const getAdmissionPage   = ()        => get('/admission/');
const updateAdmissionPage = (data)   => patch('/admission/update/', data);

const getAdmissionSections   = (p)        => get('/admission-sections/', p);
const getAdmissionSection    = (id)       => get(`/admission-sections/${id}/`);
const createAdmissionSection = (data)     => post('/admission-sections/', data);
const updateAdmissionSection = (id, d)    => patch(`/admission-sections/${id}/`, d);
const deleteAdmissionSection = (id)       => del(`/admission-sections/${id}/`);

const getGradeEquivalencies   = (p)        => get('/grade-equivalencies/', p);
const createGradeEquivalency  = (data)     => post('/grade-equivalencies/', data);
const updateGradeEquivalency  = (id, d)    => patch(`/grade-equivalencies/${id}/`, d);
const deleteGradeEquivalency  = (id)       => del(`/grade-equivalencies/${id}/`);

// ── Popup / Announcement ─────────────────────────────────────────────────

const getPopup    = ()     => get('/popup/');
const updatePopup = (data) => request('/popup/', {
  method: 'PATCH',
  body: JSON.stringify(data),
});

// ── CMS Staff Accounts Management (admin only) ────────────────────────────

const getCmsStaff        = ()        => get('/cms-staff/');
const createCmsStaff     = (data)    => post('/cms-staff/', data);
const updateCmsStaff     = (uid, d)  => patch(`/cms-staff/${uid}/`, d);
const deactivateCmsStaff = (uid)     => del(`/cms-staff/${uid}/`);

// ── Export ─────────────────────────────────────────────────────────────────

const staffService = {
  // Auth
  login, logout, isLoggedIn, getAccessToken, getStoredProfile, clearSession,

  // Self
  getMe, updateMe, getDashboard,

  // Settings
  getSettings, updateSettings,

  // Pages
  getPages, getPage, createPage, updatePage, deletePage,

  // Page Sections (nested)
  getPageSections, createPageSection, updatePageSection, deletePageSection,

  // Hero Sliders
  getHeroSliders, getHeroSlider, createHeroSlider, updateHeroSlider, deleteHeroSlider,

  // Principal Message
  getPrincipalMessages, getPrincipalMessage, createPrincipalMessage,
  updatePrincipalMessage, deletePrincipalMessage,

  // Program Cards
  getProgramCards, getProgramCard, createProgramCard, updateProgramCard, deleteProgramCard,
  getProgramFeatures, createProgramFeature, deleteProgramFeature,

  // Partner Logos
  getPartners, getPartner, createPartner, updatePartner, deletePartner,

  // News & Events
  getNews, getNewsItem, createNews, updateNews, deleteNews,

  // School Staff Members
  getStaffMembers, getStaffMember, createStaffMember, updateStaffMember, deleteStaffMember,

  // Testimonials
  getTestimonials, getTestimonial, createTestimonial, updateTestimonial, deleteTestimonial,

  // FAQs
  getFAQs, getFAQ, createFAQ, updateFAQ, deleteFAQ,

  // Menu Categories & Items
  getMenuCategories, getMenuCategory, createMenuCategory, updateMenuCategory, deleteMenuCategory,
  getMenuItems, getMenuItem, createMenuItem, updateMenuItem, deleteMenuItem,

  // Gallery
  getGallery, getAlbum, createAlbum, updateAlbum, deleteAlbum,
  getAlbumPhotos, addAlbumPhoto, deleteAlbumPhoto,

  // Contacts
  getSubmissions, getSubmission, updateSubmission, deleteSubmission,
  getContactPeople, getContactPerson, createContactPerson, updateContactPerson, deleteContactPerson,

  // Policies
  getPolicies, getPolicy, createPolicy, updatePolicy, deletePolicy,
  getPolicyPages, addPolicyPage, deletePolicyPage,

  // Admission
  getAdmissionPage, updateAdmissionPage,
  getAdmissionSections, getAdmissionSection,
  createAdmissionSection, updateAdmissionSection, deleteAdmissionSection,
  getGradeEquivalencies, createGradeEquivalency, updateGradeEquivalency, deleteGradeEquivalency,

  // Popup / Announcement
  getPopup, updatePopup,

  // CMS staff accounts
  getCmsStaff, createCmsStaff, updateCmsStaff, deactivateCmsStaff,

  // Raw HTTP helpers (for the composable)
  _patch: patch,
  _get:   get,
};

export default staffService;
